/**
 * Display helpers for personal / assigned post-it boards, cards, filters, tracking.
 */
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns';
import type { ElementWidth } from '@/types/interface-elements.types';
import type {
  AssignedPostIt,
  AssignedPostItFilters,
  AssignedRecurrence,
  PostIt,
  TrackingOccurrence,
  TrackingOccurrenceStats,
  TrackingUser,
} from '@/types/post-it.types';
import type { SchoolYear } from '@/types/school-year.types';
import {
  COLOR_MAP,
  POST_IT_STATUS_CONFIG,
  type AssignedFilter,
  type DeadlineFilter,
} from '@/components/post-its/post-it-configs';
import { describeScheduleShort } from '@/utils/schedule-description-utils';

/** School-year start/end used when resolving deadline quarters. */
export interface SchoolYearDateRange {
  startDate: Date | string;
  endDate: Date | string;
}

/** Personal board buckets: active, overdue, done, archived. */
export interface PersonalPostItPartitions {
  active: PostIt[];
  overdue: PostIt[];
  done: PostIt[];
  archived: PostIt[];
}

/** Saved drag-order slot (`id` + `order` + `section`). */
export interface PostItCustomPosition {
  id: string;
  order: number;
  section: string;
}

/** Slight rotation degrees used to stagger personal-board cards. */
const POST_IT_ROTATIONS = [-2, -1, 0, 1, 2] as const;

/** Card height from the dashboard section width. */
export function getPostItHeight(sectionWidth: ElementWidth): number {
  if (sectionWidth === '1/3') return 200;
  if (sectionWidth === '1/2') return 220;
  return 260;
}

/** Stable tilt from the post-it id (not random on each render). */
export function rotationFromId(id: string): number {
  let hash = 0;
  for (const ch of id) {
    hash = (hash + (ch.codePointAt(0) ?? 0)) % POST_IT_ROTATIONS.length;
  }
  return POST_IT_ROTATIONS[hash];
}

/** Selectable cards select; otherwise click opens edit. */
export function resolveCardActivate(
  isSelectable: boolean,
  onSelect?: () => void,
  onEdit?: () => void,
): (() => void) | undefined {
  if (isSelectable && onSelect) return onSelect;
  if (!isSelectable && onEdit) return onEdit;
  return undefined;
}

/** Screen-reader status label (card copy, not the badge catalog). */
export function statusLabel(status: PostIt['status']): string {
  if (status === 'en_cours') return 'En cours';
  if (status === 'depasse') return 'Dépassé';
  return 'Terminé';
}

/** Overdue cards use the red token so icon/text stay readable on `bg-postit-rouge`. */
export function getPostItCardColorKey(postIt: PostIt): string {
  return postIt.status === 'depasse' ? 'rouge' : postIt.color;
}

/** Tailwind background class for a personal / assigned card. */
export function getPostItCardBgClass(postIt: PostIt): string {
  if (postIt.status === 'depasse') return 'bg-postit-rouge';
  return COLOR_MAP[postIt.color] || 'bg-postit-jaune';
}

/** Formats a date as `yyyy-MM-dd` for deadline filter params. */
const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

/**
 * School-year quarters:
 * T1: 1 Sep–31 Dec | T2: 1 Jan–31 Mar | T3: 1 Apr–30 Jun | T4: 1 Jul–31 Aug
 */
export function buildSchoolYearQuarters(schoolYearStart: Date | string): Array<{
  number: number;
  startDate: Date;
  endDate: Date;
}> {
  const start = new Date(schoolYearStart);
  const startYear = start.getFullYear();
  return [
    { number: 1, startDate: new Date(startYear, 8, 1), endDate: new Date(startYear, 11, 31) },
    {
      number: 2,
      startDate: new Date(startYear + 1, 0, 1),
      endDate: new Date(startYear + 1, 2, 31),
    },
    {
      number: 3,
      startDate: new Date(startYear + 1, 3, 1),
      endDate: new Date(startYear + 1, 5, 30),
    },
    {
      number: 4,
      startDate: new Date(startYear + 1, 6, 1),
      endDate: new Date(startYear + 1, 7, 31),
    },
  ];
}

/** Fallback when there is no current school year: start in September. */
export function resolveSchoolYearStartDate(
  schoolYear?: SchoolYearDateRange | null,
  now: Date = new Date(),
): Date {
  if (schoolYear?.startDate) return new Date(schoolYear.startDate);
  const startYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return new Date(startYear, 8, 1);
}

/** Current school-year term. */
function getCurrentQuarter(
  _schoolYear?: SchoolYearDateRange | null,
  now: Date = new Date(),
): number {
  const month = now.getMonth();
  if (month >= 8) return 1;
  if (month <= 2) return 2;
  if (month <= 5) return 3;
  return 4;
}

/** Inclusive `from`/`to` covering the selected school-year quarters. */
export function getDeadlineRangeForQuarters(
  quarters: number[],
  schoolYear?: SchoolYearDateRange | null,
): { from: string; to: string } | null {
  if (quarters.length === 0) return null;

  const all = buildSchoolYearQuarters(resolveSchoolYearStartDate(schoolYear));
  const selected = all.filter((q) => quarters.includes(q.number));
  if (selected.length === 0) return null;

  const from = selected.reduce(
    (min, q) => (q.startDate < min ? q.startDate : min),
    selected[0].startDate,
  );
  const to = selected.reduce((max, q) => (q.endDate > max ? q.endDate : max), selected[0].endDate);

  return { from: fmt(from), to: fmt(to) };
}

/** Inclusive from/to dates for a named deadline period, or null if unused. */
function getDeadlineRangeForPeriod(
  period: DeadlineFilter,
  schoolYear?: SchoolYearDateRange | null,
): { from: string; to: string } | null {
  if (period === 'all' || period === 'specific_date' || period === 'quarter') return null;

  const now = new Date();

  if (period === 'week') {
    return {
      from: fmt(startOfWeek(now, { weekStartsOn: 1 })),
      to: fmt(endOfWeek(now, { weekStartsOn: 1 })),
    };
  }
  if (period === 'month') {
    return { from: fmt(startOfMonth(now)), to: fmt(endOfMonth(now)) };
  }
  if (period === 'year') {
    const quarters = buildSchoolYearQuarters(resolveSchoolYearStartDate(schoolYear, now));
    return {
      from: fmt(quarters[0].startDate),
      to: fmt(quarters[2].endDate),
    };
  }
  return null;
}

/** Next deadline range + quarter chips after a period chip change. */
export function resolveDeadlineFilterChange(
  next: DeadlineFilter,
  schoolYear?: SchoolYearDateRange | null,
): { quarterlyPeriods: number[]; range: { from: string; to: string } | null } {
  if (next === 'specific_date') {
    return { quarterlyPeriods: [], range: null };
  }
  if (next === 'quarter') {
    const current = getCurrentQuarter(schoolYear);
    return {
      quarterlyPeriods: [current],
      range: getDeadlineRangeForQuarters([current], schoolYear),
    };
  }
  return { quarterlyPeriods: [], range: getDeadlineRangeForPeriod(next, schoolYear) };
}

/** Toggle a numeric chip in a multi-select list. */
export function toggleNumberInList(list: number[], value: number): number[] {
  if (list.includes(value)) return list.filter((item) => item !== value);
  return [...list, value];
}

/** How many 280px cards fit (capped at 6). */
export function getItemsPerRow(containerWidth: number): number {
  const postItWidth = 280;
  const maxItems = Math.floor(containerWidth / postItWidth);
  return Math.max(1, Math.min(maxItems, 6));
}

/** In-progress column count (left half of the row). */
export function getActiveColumns(itemsPerRow: number): number {
  return Math.ceil(itemsPerRow / 2);
}

/** Overdue column count (right half of the row). */
export function getOverdueColumns(itemsPerRow: number): number {
  return Math.floor(itemsPerRow / 2);
}

/** Deadline ASC (nulls last), then priority, then createdAt DESC. */
export function sortPostItsByDeadline(items: PostIt[]): PostIt[] {
  return [...items].sort((a, b) => {
    if (a.deadline && b.deadline) {
      const diff = new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
      if (diff !== 0) return diff;
    } else if (a.deadline && !b.deadline) {
      return -1;
    } else if (!a.deadline && b.deadline) {
      return 1;
    }

    if (a.isPriority && !b.isPriority) return -1;
    if (!a.isPriority && b.isPriority) return 1;

    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

/** Done cards: priority first, then deadline, then createdAt DESC. */
export function sortDonePostIts(items: PostIt[]): PostIt[] {
  return [...items].sort((a, b) => {
    if (a.isPriority && !b.isPriority) return -1;
    if (!a.isPriority && b.isPriority) return 1;

    if (a.deadline && b.deadline) {
      return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
    }
    if (a.deadline && !b.deadline) return -1;
    if (!a.deadline && b.deadline) return 1;

    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

/** Archived cards newest-updated first. */
export function sortArchivedPostIts(items: PostIt[]): PostIt[] {
  return [...items].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

/** Custom saved positions when `sortBy === 'custom'`; else deadline sort. */
export function sortPersonalPostIts(
  postIts: PostIt[],
  positions: Array<{ id: string; order: number }> | undefined,
  sortBy: string | undefined,
): PostIt[] {
  const useCustom = sortBy === 'custom' && Array.isArray(positions) && positions.length > 0;
  if (!useCustom) return sortPostItsByDeadline(postIts);

  return [...postIts].sort((a, b) => {
    const orderA = positions.find((o) => o.id === a.id)?.order ?? 999;
    const orderB = positions.find((o) => o.id === b.id)?.order ?? 999;
    return orderA - orderB;
  });
}

/** Split a sorted personal list into status buckets. */
export function partitionPersonalPostIts(sortedPostIts: PostIt[]): PersonalPostItPartitions {
  return {
    active: sortedPostIts.filter((p) => p.status === 'en_cours' && !p.isArchived),
    overdue: sortedPostIts.filter((p) => p.status === 'depasse' && !p.isArchived),
    done: sortedPostIts.filter((p) => p.status === 'done' && !p.isArchived),
    archived: sortedPostIts.filter((p) => p.isArchived),
  };
}

/** Collapse cap: 2 rows per column, not the whole grid. */
export function getCollapsedVisibleCounts(itemsPerRow: number): {
  maxActiveVisible: number;
  maxOverdueVisible: number;
} {
  return {
    maxActiveVisible: 2 * getActiveColumns(itemsPerRow),
    maxOverdueVisible: 2 * getOverdueColumns(itemsPerRow),
  };
}

/** Persist current list order as custom positions. */
export function buildCustomPositions(postIts: PostIt[]): PostItCustomPosition[] {
  return postIts.map((postIt, index) => ({
    id: postIt.id,
    order: index,
    section: postIt.status,
  }));
}

/** DnD only when both cards share the same status. */
export function canReorderWithinStatus(
  activeItem: PostIt | undefined,
  overItem: PostIt | undefined,
): boolean {
  if (!activeItem || !overItem) return false;
  return activeItem.status === overItem.status;
}

/** Move `activeId` to `overId`'s index; unchanged when either id is missing. */
export function reorderPostIts(
  sortedPostIts: PostIt[],
  activeId: string,
  overId: string,
): PostIt[] {
  const oldIndex = sortedPostIts.findIndex((p) => p.id === activeId);
  const newIndex = sortedPostIts.findIndex((p) => p.id === overId);
  if (oldIndex < 0 || newIndex < 0) return sortedPostIts;
  const next = [...sortedPostIts];
  const [movedItem] = next.splice(oldIndex, 1);
  next.splice(newIndex, 0, movedItem);
  return next;
}

/** Maps an {@link AssignedPostIt} template to the {@link PostIt} shape {@link PostItCard} expects. */
export function toCardShape(t: AssignedPostIt): PostIt {
  return {
    id: t.id,
    companyId: t.companyId,
    createdById: t.createdById,
    createdByName: t.createdByName,
    title: t.title,
    thematic: t.thematic,
    content: t.content,
    color: t.color,
    isPriority: t.isPriority,
    isArchived: false,
    status: 'en_cours',
    deadline: t.specificDateFrom ? new Date(t.specificDateFrom) : null,
    visibleFrom: null,
    createdAt: new Date(t.createdAt),
    updatedAt: new Date(t.updatedAt),
    userPermission: isAssignedTemplateComplete(t) ? 'read' : 'owner',
    isDefault: true,
  };
}

/** Quarters from advanced scheduleConfig (targetQuarters). */
export function quartersFromScheduleConfig(t: AssignedPostIt): number[] {
  const cfg = t.scheduleConfig;
  if (cfg?.type !== 'quarterly') return [];
  const target = (cfg.options as { targetQuarters?: number[] | 'all' } | undefined)?.targetQuarters;
  if (target === 'all') return [1, 2, 3, 4];
  return Array.isArray(target) ? target : [];
}

/**
 * Complete = role(s) + schedule (advanced scheduleConfig OR legacy recurrence).
 * Quarterly also needs at least one period.
 */
export function isAssignedTemplateComplete(t: AssignedPostIt): boolean {
  if ((t.targetRoleIds?.length ?? 0) === 0) return false;

  if (t.scheduleConfig) {
    if (t.scheduleConfig.type === 'quarterly') {
      return quartersFromScheduleConfig(t).length > 0 || (t.quarterlyPeriods?.length ?? 0) > 0;
    }
    return true;
  }

  return !!t.recurrence && (t.recurrence !== 'quarterly' || (t.quarterlyPeriods?.length ?? 0) > 0);
}

/** Best-effort date for sorting assigned templates (deadline / start). */
export function getTemplateSortTime(t: AssignedPostIt): number {
  if (t.specificDateFrom) return new Date(t.specificDateFrom).getTime();
  if (t.specificDateTo) return new Date(t.specificDateTo).getTime();
  if (t.scheduleConfig?.type === 'custom_dates') {
    const opts = t.scheduleConfig.options as { dateFrom?: string; dateTo?: string };
    const raw = opts.dateTo || opts.dateFrom;
    if (raw) return new Date(raw).getTime();
  }
  return new Date(t.createdAt).getTime();
}

/** Short French recurrence label for assigned-template footer. */
export function describeAssignedRecurrenceShort(recurrence: AssignedRecurrence): string {
  switch (recurrence) {
    case 'weekly':
      return 'Hebdo';
    case 'monthly':
      return 'Mensuel';
    case 'quarterly':
      return 'Trimestriel';
    case 'annually':
      return 'Annuel';
    case 'specific_date':
      return 'Date spécifique';
    default:
      return '';
  }
}

/** Footer: first target role + short schedule / recurrence. */
export function buildAssignedFooterBadge(
  t: AssignedPostIt,
  rolesMap: Record<string, string>,
): string {
  const rolePart = t.targetRoleIds?.[0] ? (rolesMap[t.targetRoleIds[0]] ?? '') : '';
  let schedulePart = '';
  if (t.scheduleConfig) {
    schedulePart = describeScheduleShort(t.scheduleConfig);
  } else if (t.recurrence) {
    schedulePart = describeAssignedRecurrenceShort(t.recurrence);
  }
  return [rolePart, schedulePart].filter(Boolean).join(' · ');
}

/** Incomplete first, then chronological within each group. */
export function sortAssignedTemplates(templates: AssignedPostIt[]): AssignedPostIt[] {
  return [...templates].sort((a, b) => {
    const completeDiff =
      Number(isAssignedTemplateComplete(b)) - Number(isAssignedTemplateComplete(a));
    if (completeDiff !== 0) return completeDiff;
    return getTemplateSortTime(a) - getTemplateSortTime(b);
  });
}

/** Incomplete templates first (`pending`), then complete (`assigned`). */
export function splitAssignedTemplates(templates: AssignedPostIt[]): {
  pending: AssignedPostIt[];
  assigned: AssignedPostIt[];
} {
  return {
    pending: templates.filter((t) => !isAssignedTemplateComplete(t)),
    assigned: templates.filter((t) => isAssignedTemplateComplete(t)),
  };
}

/** Default occurrence: current provisioned, else last past, else nearest future. */
export function pickCurrentTrackingOccurrence(
  occurrences: TrackingOccurrence[] | undefined,
  now: Date = new Date(),
): TrackingOccurrence | undefined {
  if (!occurrences?.length) return undefined;

  const currentProvisioned = occurrences
    .filter((o) => o.provisioned && new Date(o.date) >= now)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
  if (currentProvisioned) return currentProvisioned;

  const mostRecentPast = occurrences
    .filter((o) => o.provisioned && new Date(o.date) < now)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  if (mostRecentPast) return mostRecentPast;

  const closestFuture = occurrences
    .filter((o) => new Date(o.date) >= now)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
  return closestFuture ?? occurrences.at(-1);
}

/** Selected period, else the current occurrence's `periodKey`. */
export function getEffectivePeriodKey(
  selectedPeriodKey: string | null,
  currentOccurrence: TrackingOccurrence | undefined,
): string | null {
  return selectedPeriodKey ?? currentOccurrence?.periodKey ?? null;
}

/** Done / overdue / pending counts; zeros when no occurrence. */
export function getTrackingOccurrenceStats(
  selectedOccurrence: TrackingOccurrence | null,
): Pick<TrackingOccurrenceStats, 'done' | 'overdue' | 'pending'> {
  if (!selectedOccurrence) return { done: 0, overdue: 0, pending: 0 };
  return {
    done: selectedOccurrence.stats.done,
    overdue: selectedOccurrence.stats.overdue,
    pending: selectedOccurrence.stats.pending,
  };
}

/** Two-letter initials, or `?`. */
export function getUserInitials(firstName?: string | null, lastName?: string | null): string {
  return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase() || '?';
}

/** Locale date; sentinel years before 2000 render as an em dash. */
export function formatTrackingDate(date: Date | null): string {
  if (!date) return '—';
  const d = new Date(date);
  if (d.getFullYear() < 2000) return '—';
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Excel header + one row per tracking user. */
export function buildTrackingExportRows(users: TrackingUser[]): string[][] {
  const headers = ['Nom', 'Prénom', 'Statut', 'Échéance', 'Complété le'];
  const rows = users.map((user) => [
    user.lastName,
    user.firstName,
    POST_IT_STATUS_CONFIG[user.status]?.label ?? user.status,
    formatTrackingDate(user.deadline),
    user.status === 'done' ? formatTrackingDate(user.updatedAt) : '—',
  ]);
  return [headers, ...rows];
}

/** `suivi_assignations_YYYY-MM-DD.xlsx`. */
export function getTrackingExportFileName(now: Date = new Date()): string {
  return `suivi_assignations_${now.toISOString().slice(0, 10)}.xlsx`;
}

/** Done / total as a 0–100 percent. */
export function getCompletionPercentage(occ: TrackingOccurrence): number {
  if (occ.stats.total === 0) return 0;
  return Math.round((occ.stats.done / occ.stats.total) * 100);
}

/** Progress-bar token from completion percent (80 / 50 thresholds). */
export function getTrackingProgressColor(percentage: number): string {
  if (percentage >= 80) return POST_IT_STATUS_CONFIG.done.dotColor;
  if (percentage >= 50) return POST_IT_STATUS_CONFIG.en_cours.dotColor;
  return POST_IT_STATUS_CONFIG.depasse.dotColor;
}

/** Provisioned occurrence whose date is today or later. */
export function isCurrentTrackingOccurrence(
  occ: TrackingOccurrence,
  now: Date = new Date(),
): boolean {
  return occ.provisioned && new Date(occ.date) >= now;
}

/** Chronological occurrence list. */
export function sortTrackingOccurrences(occurrences: TrackingOccurrence[]): TrackingOccurrence[] {
  return [...occurrences].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

/** List filters: a date range overrides the status chip. */
export function buildAssignedPostItsQueryArgs(args: {
  filter: AssignedFilter;
  endDateFrom?: string;
  endDateTo?: string;
  quarterlyPeriods?: number[];
  mode?: AssignedPostItFilters['mode'];
}): AssignedPostItFilters {
  const hasDateFilter = !!(args.endDateFrom || args.endDateTo);
  return {
    filter: hasDateFilter ? 'all' : args.filter,
    endDateFrom: args.endDateFrom,
    endDateTo: args.endDateTo,
    quarterlyPeriods:
      args.filter === 'quarterly' && !hasDateFilter ? args.quarterlyPeriods : undefined,
    mode: args.mode,
  };
}

/** Role id → label map for assigned-template footer badges. */
export function buildRolesIdToLabelMap(
  roles: Array<{ id: string; label: string }>,
): Record<string, string> {
  return roles.reduce<Record<string, string>>((acc, role) => {
    acc[role.id] = role.label;
    return acc;
  }, {});
}

/** Current school year, else the first row, else null. */
export function pickCurrentSchoolYearDateRange(
  schoolYearsRes?: { data?: SchoolYear[] } | null,
): SchoolYearDateRange | null {
  const schoolYear =
    schoolYearsRes?.data?.find((year) => year.isCurrent) ?? schoolYearsRes?.data?.[0];
  if (!schoolYear) return null;
  return { startDate: schoolYear.startDate, endDate: schoolYear.endDate };
}
